import { useEffect } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as fbSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  updateProfile,
  type User as FirebaseUser,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import i18n, { isSupportedLocale } from "@/lib/i18n";
import { defaultCurrencyForLocale } from "@amazing-wedding/shared-types";
import {
  buildEmailVerificationActionCodeSettings,
  buildPasswordResetActionCodeSettings,
} from "@/lib/auth-action-code-settings";
import { auth, googleProvider, db } from "@/lib/firebase";
import { identifyUser, resetAnalyticsUser, track } from "@/lib/analytics";
import { useAuthStore } from "@/stores/auth-store";
import { reportError } from "@/lib/report-error";
import {
  isGooglePopupBlockedError,
  isGoogleRedirectFallbackError,
  prefersGoogleAuthRedirect,
} from "@/features/auth/google-auth-strategy";
import { waitForAuthSynced } from "@/features/auth/wait-for-auth-sync";

/** Firebase redirect result must be consumed once per full page load (Strict Mode safe). */
let googleRedirectResultPromise: ReturnType<typeof getRedirectResult> | null = null;

function getGoogleRedirectResultOnce() {
  googleRedirectResultPromise ??= getRedirectResult(auth);
  return googleRedirectResultPromise;
}

/**
 * Resolves whether a user is on the platform admin allowlist (/admins/{email},
 * doc id = lowercased email). A module-level sequence number discards stale
 * results — if the user signs out mid-lookup, the in-flight resolution is
 * ignored so it can't flip `isAdmin` back on.
 */
let adminResolveSeq = 0;
async function resolveAdmin(
  u: FirebaseUser | null,
  setIsAdmin: (b: boolean) => void,
  setAdminResolved: (b: boolean) => void,
) {
  const seq = ++adminResolveSeq;
  const email = u?.email?.toLowerCase() ?? null;
  if (!email) {
    if (seq === adminResolveSeq) {
      setIsAdmin(false);
      setAdminResolved(true);
    }
    return;
  }
  try {
    const snap = await getDoc(doc(db, "admins", email));
    if (seq === adminResolveSeq) {
      setIsAdmin(snap.exists());
      setAdminResolved(true);
    }
  } catch (err) {
    reportError("admin", err);
    if (seq === adminResolveSeq) {
      setIsAdmin(false);
      setAdminResolved(true);
    }
  }
}

async function syncAnalyticsIdentity(u: FirebaseUser | null): Promise<void> {
  if (!u) {
    resetAnalyticsUser();
    return;
  }
  try {
    const snap = await getDoc(doc(db, "users", u.uid));
    const data = snap.data() as { plan?: string; locale?: string } | undefined;
    identifyUser(u.uid, {
      email: u.email,
      name: u.displayName,
      plan: data?.plan ?? "free",
      locale: typeof data?.locale === "string" ? data.locale : undefined,
    });
  } catch (err) {
    reportError("analytics-identify", err);
    identifyUser(u.uid, { email: u.email, name: u.displayName });
  }
}

/**
 * On sign-in, reflect the user's saved `locale` on /users/{uid} into i18next.
 * The user-doc value wins over any browser/localStorage detection so a user
 * who set EN on one device gets EN on the next. Stale results from a
 * previous user are discarded via a sequence number.
 */
let localeSyncSeq = 0;
async function syncLocaleFromUserDoc(u: FirebaseUser | null): Promise<void> {
  const seq = ++localeSyncSeq;
  if (!u) return;
  try {
    const snap = await getDoc(doc(db, "users", u.uid));
    if (seq !== localeSyncSeq) return;
    const data = snap.data() as { locale?: unknown } | undefined;
    const locale = data?.locale;
    if (isSupportedLocale(locale) && i18n.resolvedLanguage?.slice(0, 2) !== locale) {
      await i18n.changeLanguage(locale);
    }
  } catch (err) {
    reportError("locale-sync", err);
  }
}

/**
 * Subscribes the auth store to Firebase auth state.
 * Mounted once at the top of App.tsx.
 */
export function useAuthBootstrap() {
  const setFirebaseUser = useAuthStore((s) => s.setFirebaseUser);
  const setInitialized = useAuthStore((s) => s.setInitialized);
  const setIsAdmin = useAuthStore((s) => s.setIsAdmin);
  const setAdminResolved = useAuthStore((s) => s.setAdminResolved);
  useEffect(() => {
    let unsub: (() => void) | undefined;

    void (async () => {
      try {
        const redirectResult = await getGoogleRedirectResultOnce();
        if (redirectResult?.user) {
          await seedUserAfterGoogleSignIn(redirectResult.user);
          await waitForAuthSynced(redirectResult.user.uid);
        }
      } catch (err) {
        reportError("auth-redirect", err);
      }

      let prevUid: string | null | undefined;
      unsub = onAuthStateChanged(auth, (u) => {
        const uid = u?.uid ?? null;
        setFirebaseUser(u);
        setInitialized(true);
        setAdminResolved(false);
        // End any impersonation whenever the session identity changes — both
        // sign-out (uid → null) and a direct account switch (uid → other uid)
        // — so a previous admin's "view as" can't leak into a new session. The
        // first emission after a page load is skipped (prevUid is undefined),
        // so impersonation still persists across reloads via sessionStorage.
        if (prevUid !== undefined && uid !== prevUid) {
          useAuthStore.getState().setImpersonatedUserId(null);
        }
        prevUid = uid;
        void resolveAdmin(u, setIsAdmin, setAdminResolved);
        void syncLocaleFromUserDoc(u);
        void syncAnalyticsIdentity(u);
      });
    })();

    return () => {
      unsub?.();
    };
  }, [setFirebaseUser, setInitialized, setIsAdmin, setAdminResolved]);
}

/**
 * Re-stamps the PostHog person with the current `plan` whenever it changes
 * (free → premium after a Mollie webhook flip, premium → free after cancel /
 * expiry). Identify-on-auth-state-change in `useAuthBootstrap` only fires once
 * per sign-in, so without this the person property `plan` drifts. Distinct_id
 * is unchanged — identify with the same uid is a property update, not a merge.
 */
export function useAnalyticsPlanSync(uid: string | null, plan: string | null): void {
  useEffect(() => {
    if (!uid || !plan) return;
    identifyUser(uid, { plan });
  }, [uid, plan]);
}

export function useAuth() {
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const initialized = useAuthStore((s) => s.initialized);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const adminResolved = useAuthStore((s) => s.adminResolved);
  return {
    firebaseUser,
    initialized,
    isAdmin,
    adminResolved,
    isSignedIn: !!firebaseUser,
    user: firebaseUser
      ? {
          id: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          emailVerified: firebaseUser.emailVerified,
        }
      : null,
  };
}

/**
 * The uid the app should read data for. Normally the signed-in user, but when
 * an admin is impersonating ("view as") it resolves to the target user so
 * `useWedding` / `useCurrentUser` — and every hook downstream of them — render
 * that user's data. Always falls back to the real user for non-admins, so the
 * impersonation override can never escalate a non-admin's access.
 */
export function useEffectiveUid(): string | null {
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const impersonatedUserId = useAuthStore((s) => s.impersonatedUserId);
  if (isAdmin && impersonatedUserId) return impersonatedUserId;
  return firebaseUser?.uid ?? null;
}

/** Current "view as" state for the admin impersonation banner + controls. */
export function useImpersonation(): {
  isImpersonating: boolean;
  impersonatedUserId: string | null;
} {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const impersonatedUserId = useAuthStore((s) => s.impersonatedUserId);
  const active = isAdmin ? impersonatedUserId : null;
  return { isImpersonating: !!active, impersonatedUserId: active };
}

/** Begin viewing the app (read-only) as another user. Admin-gated by callers. */
export function startImpersonation(uid: string): void {
  useAuthStore.getState().setImpersonatedUserId(uid);
}

/** Return to the admin's own session. */
export function stopImpersonation(): void {
  useAuthStore.getState().setImpersonatedUserId(null);
}

interface EnsureUserDocFields {
  displayName: string | null;
  coupleName1: string | null;
  coupleName2: string | null;
}

async function ensureUserDoc(
  uid: string,
  email: string | null,
  fields: EnsureUserDocFields,
): Promise<boolean> {
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    const activeLang = i18n.resolvedLanguage?.slice(0, 2);
    const seedLocale = isSupportedLocale(activeLang) ? activeLang : "pl";
    await setDoc(ref, {
      email,
      displayName: fields.displayName,
      coupleName1: fields.coupleName1,
      coupleName2: fields.coupleName2,
      photoURL: null,
      locale: seedLocale,
      currency: defaultCurrencyForLocale(seedLocale),
      plan: "free",
      partnerId: null,
      emailVerified: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    track("user_signed_up", {
      source: "client",
      email,
      display_name: fields.displayName,
    });
    return true;
  }
  return false;
}

function trackSignupCompleted(method: "email" | "google", isNewUser: boolean): void {
  track("signup_completed", { method, is_new_user: isNewUser });
}

export async function signInWithEmail(email: string, password: string, remember: boolean) {
  await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
  const cred = await signInWithEmailAndPassword(auth, email, password);
  // Returning user — seed couple fields as null if doc is somehow missing;
  // they'll have been filled at registration time on the happy path.
  await ensureUserDoc(cred.user.uid, cred.user.email, {
    displayName: cred.user.displayName,
    coupleName1: null,
    coupleName2: null,
  });
  await waitForAuthSynced(cred.user.uid);
  return cred.user;
}

interface RegisterWithEmailArgs {
  email: string;
  password: string;
  /** Just the registering user's first name. Onboarding step 1 collects
   *  the partner's name (`coupleName2`) right after. */
  coupleName1: string;
}

export async function registerWithEmail(args: RegisterWithEmailArgs) {
  await setPersistence(auth, browserLocalPersistence);
  const cred = await createUserWithEmailAndPassword(auth, args.email, args.password);
  await updateProfile(cred.user, { displayName: args.coupleName1 });
  const isNewUser = await ensureUserDoc(cred.user.uid, cred.user.email, {
    displayName: args.coupleName1,
    coupleName1: args.coupleName1,
    coupleName2: null,
  });
  trackSignupCompleted("email", isNewUser);
  auth.languageCode = i18n.resolvedLanguage?.slice(0, 2) ?? "pl";
  await sendEmailVerification(cred.user, buildEmailVerificationActionCodeSettings());
  await waitForAuthSynced(cred.user.uid);
  return cred.user;
}

async function seedUserAfterGoogleSignIn(user: FirebaseUser): Promise<boolean> {
  // Google gives us a single display name — seed partner 1 from it, leave
  // partner 2 null so Onboarding step 1 collects it.
  const isNewUser = await ensureUserDoc(user.uid, user.email, {
    displayName: user.displayName,
    coupleName1: user.displayName,
    coupleName2: null,
  });
  if (isNewUser) {
    trackSignupCompleted("google", true);
  }
  return isNewUser;
}

export async function signInWithGoogle() {
  await setPersistence(auth, browserLocalPersistence);
  if (prefersGoogleAuthRedirect()) {
    await signInWithRedirect(auth, googleProvider);
    return null;
  }
  try {
    const cred = await signInWithPopup(auth, googleProvider);
    await seedUserAfterGoogleSignIn(cred.user);
    await waitForAuthSynced(cred.user.uid);
    return cred.user;
  } catch (err) {
    if (isGooglePopupBlockedError(err) || isGoogleRedirectFallbackError(err)) {
      await signInWithRedirect(auth, googleProvider);
      return null;
    }
    throw err;
  }
}

export async function resetPassword(email: string) {
  const normalizedEmail = email.trim().toLowerCase();
  auth.languageCode = i18n.resolvedLanguage?.slice(0, 2) ?? "pl";
  await sendPasswordResetEmail(auth, normalizedEmail, buildPasswordResetActionCodeSettings());
}

export async function signOut() {
  await fbSignOut(auth);
}
